import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, Lock, Shield, Briefcase, ArrowLeft, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginType, setLoginType] = useState('sales'); // 'investor-admin', 'hr', or 'sales'
  const [error, setError] = useState('');
  
  const [investorAdminForm, setInvestorAdminForm] = useState({ user_id: '', password: '' });
  const [hrForm, setHrForm] = useState({ user_id: '', password: '' });
  const [salesForm, setSalesForm] = useState({ user_id: '', password: '' });

  const handleInvestorAdminLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(investorAdminForm)
      });
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('userRole', 'admin');
        navigate('/admin/dashboard');
      } else {
        setError(data.detail || 'Invalid credentials');
      }
    } catch (error) {
      setError('Error connecting to server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleHRLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/hr/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(hrForm)
      });
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('hrToken', data.token);
        localStorage.setItem('hrUserId', data.user_id);
        localStorage.setItem('hrUserName', data.name);
        localStorage.setItem('userRole', 'hr');
        navigate('/hr/admin');
      } else {
        setError(data.detail || 'Invalid credentials');
      }
    } catch (error) {
      setError('Error connecting to server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalesLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/sales/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(salesForm)
      });
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('salesToken', data.token);
        localStorage.setItem('salesUserId', data.user_id);
        localStorage.setItem('salesUserName', data.name);
        localStorage.setItem('userRole', 'sales_admin');
        navigate('/sales/admin');
      } else {
        setError(data.detail || 'Invalid credentials');
      }
    } catch (error) {
      setError('Error connecting to server');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 flex items-center justify-center p-4">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Back to Home Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Website</span>
        </Link>

        <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Admin Access</CardTitle>
            <CardDescription>STEAM-IE Management Portal</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Login Type Toggle */}
            <div className="grid grid-cols-3 gap-2 mb-6">
              <Button 
                variant={loginType === 'sales' ? 'default' : 'outline'} 
                className={`${loginType === 'sales' ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
                onClick={() => { setLoginType('sales'); setError(''); }}
                data-testid="sales-admin-tab"
                size="sm"
              >
                <TrendingUp className="w-4 h-4 mr-1" /> Sales
              </Button>
              <Button 
                variant={loginType === 'hr' ? 'default' : 'outline'} 
                className={`${loginType === 'hr' ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
                onClick={() => { setLoginType('hr'); setError(''); }}
                data-testid="hr-admin-tab"
                size="sm"
              >
                <Briefcase className="w-4 h-4 mr-1" /> HR
              </Button>
              <Button 
                variant={loginType === 'investor-admin' ? 'default' : 'outline'} 
                className={`${loginType === 'investor-admin' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                onClick={() => { setLoginType('investor-admin'); setError(''); }}
                data-testid="investor-admin-tab"
                size="sm"
              >
                <Shield className="w-4 h-4 mr-1" /> Investor
              </Button>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            {/* Sales Admin Login Form */}
            {loginType === 'sales' && (
              <form onSubmit={handleSalesLogin} className="space-y-4" data-testid="sales-admin-form">
                <div className="bg-orange-50 p-3 rounded-lg mb-4">
                  <p className="text-sm text-orange-700">
                    <strong>Sales Admin Portal</strong><br/>
                    Manage leads, track employee performance & daily reports
                  </p>
                </div>
                <div>
                  <Label>Sales Admin ID</Label>
                  <Input 
                    value={salesForm.user_id} 
                    onChange={(e) => setSalesForm({...salesForm, user_id: e.target.value})} 
                    required 
                    placeholder="Enter Sales Admin ID"
                    data-testid="sales-admin-user-id"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input 
                    type="password" 
                    value={salesForm.password} 
                    onChange={(e) => setSalesForm({...salesForm, password: e.target.value})} 
                    required 
                    placeholder="Enter password"
                    data-testid="sales-admin-password"
                  />
                </div>
                <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700" disabled={isLoading} data-testid="sales-admin-login-btn">
                  {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Login to Sales Admin
                </Button>
              </form>
            )}

            {/* Investor Admin Login Form */}
            {loginType === 'investor-admin' && (
              <form onSubmit={handleInvestorAdminLogin} className="space-y-4" data-testid="investor-admin-form">
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <p className="text-sm text-blue-700">
                    <strong>Investor Admin Portal</strong><br/>
                    Manage investors, shares, dividends & company news
                  </p>
                </div>
                <div>
                  <Label>Admin User ID</Label>
                  <Input 
                    value={investorAdminForm.user_id} 
                    onChange={(e) => setInvestorAdminForm({...investorAdminForm, user_id: e.target.value})} 
                    required 
                    placeholder="Enter admin ID"
                    data-testid="investor-admin-user-id"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input 
                    type="password" 
                    value={investorAdminForm.password} 
                    onChange={(e) => setInvestorAdminForm({...investorAdminForm, password: e.target.value})} 
                    required 
                    placeholder="Enter password"
                    data-testid="investor-admin-password"
                  />
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isLoading} data-testid="investor-admin-login-btn">
                  {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Login to Investor Admin
                </Button>
              </form>
            )}

            {/* HR Admin Login Form */}
            {loginType === 'hr' && (
              <form onSubmit={handleHRLogin} className="space-y-4" data-testid="hr-admin-form">
                <div className="bg-teal-50 p-3 rounded-lg mb-4">
                  <p className="text-sm text-teal-700">
                    <strong>HR Admin Portal</strong><br/>
                    Manage employees, onboarding & documents
                  </p>
                </div>
                <div>
                  <Label>HR User ID</Label>
                  <Input 
                    value={hrForm.user_id} 
                    onChange={(e) => setHrForm({...hrForm, user_id: e.target.value})} 
                    required 
                    placeholder="Enter HR User ID"
                    data-testid="hr-admin-user-id"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input 
                    type="password" 
                    value={hrForm.password} 
                    onChange={(e) => setHrForm({...hrForm, password: e.target.value})} 
                    required 
                    placeholder="Enter password"
                    data-testid="hr-admin-password"
                  />
                </div>
                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={isLoading} data-testid="hr-admin-login-btn">
                  {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Login to HR Admin
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-6">
          © {new Date().getFullYear()} STEAM-IE. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
